import type { QueryClient } from "@tanstack/react-query";
import { categoriesAndAccountsQueryKeys } from "./useCategoriesAndAccountsQuery";
import {
  financialOverviewQueryKey,
  financialQueryKeys,
} from "./financialQueryKeys";
import { mediumFinancialTransparencyQueryKey } from "./useMediumFinancialTransparencyQuery";

export type FinancialOwnerScope = {
  terreiroId?: string;
  mediumId?: string;
};

/**
 * Refreshes every cached view of account balances after a financial write.
 * Contas sums `currentBalance` from the bank-account list. The register form
 * keeps a second copy of that list, and the painel reads overview/report totals.
 * Each id that the caller has is refreshed; terreiro and personal caches differ.
 */
export const invalidateAccountBalanceQueries = (
  qc: QueryClient,
  scope: FinancialOwnerScope,
) => {
  const tasks: Array<Promise<unknown>> = [];

  if (scope.terreiroId) {
    const terreiroId = scope.terreiroId;
    tasks.push(
      qc.invalidateQueries({
        queryKey: financialQueryKeys.bankAccounts(terreiroId),
      }),
      qc.invalidateQueries({
        queryKey: categoriesAndAccountsQueryKeys.bankAccounts(
          terreiroId,
          undefined,
        ),
      }),
      qc.invalidateQueries({
        queryKey: financialOverviewQueryKey({ terreiroId }),
      }),
      // Prefix covers every month/year already cached for this terreiro.
      qc.invalidateQueries({
        queryKey: ["terreiro", terreiroId, "financial-report"],
      }),
    );
  }

  if (scope.mediumId) {
    const mediumId = scope.mediumId;
    tasks.push(
      qc.invalidateQueries({
        queryKey: financialQueryKeys.mediumBankAccounts(mediumId),
      }),
      qc.invalidateQueries({
        queryKey: categoriesAndAccountsQueryKeys.bankAccounts(
          undefined,
          mediumId,
        ),
      }),
      qc.invalidateQueries({
        queryKey: financialOverviewQueryKey({ mediumId }),
      }),
      qc.invalidateQueries({
        queryKey: mediumFinancialTransparencyQueryKey(mediumId),
      }),
    );
  }

  return Promise.all(tasks);
};
